import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, Divider, Tabs, Tab, CircularProgress,
    Button, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { loadFromIndexedDB } from './indexedDBUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const ReportDashboard = () => {
    const [localOrders, setLocalOrders] = useState([]);
    const [indexedDBOrders, setIndexedDBOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);

    const [filterType, setFilterType] = useState("today");
    const [filterValue, setFilterValue] = useState(dayjs().format('YYYY-MM-DD'));

    useEffect(() => {
        const loadOrders = async () => {
            try {
                const storedLocal = JSON.parse(localStorage.getItem('billHistory')) || [];
                setLocalOrders(storedLocal);

                const indexedOrders = await loadFromIndexedDB();
                setIndexedDBOrders(indexedOrders);
            } catch (error) {
                console.error('Error loading orders:', error);
            } finally {
                setLoading(false);
            }
        };
        loadOrders();
    }, []);

    const getFilteredOrders = () => {
        const orders = tab === 0 ? localOrders : indexedDBOrders;

        return orders.filter(order => {
            const date = dayjs(order.timestamp);
            const today = dayjs();
            const yesterday = dayjs().subtract(1, 'day');

            if (filterType === 'today') {
                return date.isSame(today, 'day');
            } else if (filterType === 'yesterday') {
                return date.isSame(yesterday, 'day');
            } else if (filterType === 'date') {
                return date.format('YYYY-MM-DD') === filterValue;
            } else if (filterType === 'month') {
                return date.format('YYYY-MM') === filterValue;
            } else if (filterType === 'year') {
                return date.format('YYYY') === filterValue;
            }

            return true;
        });
    };

    const filteredOrders = getFilteredOrders();

    const handleDownloadPDF = () => {
        if (filteredOrders.length === 0) {
            alert("No orders found for the selected range.");
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text("Filtered Orders Report", 14, 20);
        doc.setFontSize(10);
        doc.text(`Filter: ${filterType.toUpperCase()} - ${filterValue}`, 14, 28);
        doc.text(`Generated on: ${dayjs().format('YYYY-MM-DD HH:mm')}`, 14, 34);

        const rows = filteredOrders.map(order => [
            order.orderId || 'N/A',
            dayjs(order.timestamp).format('YYYY-MM-DD HH:mm'),
            `Rs ${parseFloat(order.total || 0).toFixed(2)}`
        ]);

        autoTable(doc, {
            startY: 40,
            head: [['Order ID', 'Date', 'Total']],
            body: rows,
            styles: { fontSize: 10 },
        });

        const totalIncome = filteredOrders.reduce((sum, order) => {
            return sum + parseFloat(order.total || 0);
        }, 0);

        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total Income: Rs ${totalIncome.toFixed(2)}`, 14, finalY);

        doc.save(`Orders_${filterType}_${filterValue}.pdf`);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Order Report Dashboard
            </Typography>

            <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
                <Tab label="From localStorage" />
                <Tab label="From IndexedDB" />
            </Tabs>

            {/* Filter Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl>
                    <InputLabel>Filter Type</InputLabel>
                    <Select
                        value={filterType}
                        label="Filter Type"
                        onChange={(e) => {
                            const type = e.target.value;
                            setFilterType(type);
                            if (type === 'today') {
                                setFilterValue(dayjs().format('YYYY-MM-DD'));
                            } else if (type === 'yesterday') {
                                setFilterValue(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
                            } else if (type === 'month') {
                                setFilterValue(dayjs().format('YYYY-MM'));
                            } else if (type === 'year') {
                                setFilterValue(dayjs().format('YYYY'));
                            } else if (type === 'date') {
                                setFilterValue(dayjs().format('YYYY-MM-DD'));
                            }
                        }}
                    >
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="yesterday">Yesterday</MenuItem>
                        <MenuItem value="date">Specific Date</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="year">Year</MenuItem>
                    </Select>
                </FormControl>

                {(filterType === 'date') && (
                    <FormControl>
                        <InputLabel>Date</InputLabel>
                        <Select
                            value={filterValue}
                            label="Date"
                            onChange={(e) => setFilterValue(e.target.value)}
                        >
                            {Array.from({ length: 30 }, (_, i) => {
                                const date = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
                                return <MenuItem key={i} value={date}>{date}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                )}

                {(filterType === 'month') && (
                    <FormControl>
                        <InputLabel>Month</InputLabel>
                        <Select
                            value={filterValue}
                            label="Month"
                            onChange={(e) => setFilterValue(e.target.value)}
                        >
                            {Array.from({ length: 12 }, (_, i) => {
                                const month = dayjs().subtract(i, 'month').format('YYYY-MM');
                                return <MenuItem key={i} value={month}>{month}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                )}

                {(filterType === 'year') && (
                    <FormControl>
                        <InputLabel>Year</InputLabel>
                        <Select
                            value={filterValue}
                            label="Year"
                            onChange={(e) => setFilterValue(e.target.value)}
                        >
                            {Array.from({ length: 5 }, (_, i) => {
                                const year = dayjs().year() - i;
                                return <MenuItem key={i} value={year.toString()}>{year}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                )}

                <Button variant="contained" onClick={handleDownloadPDF}>
                    Download PDF
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    {filteredOrders.length === 0 ? (
                        <Typography>No orders found for the selected range.</Typography>
                    ) : (
                        filteredOrders.map((order, i) => (
                            <Card key={i} sx={{ mb: 2, p: 2 }}>
                                <Typography><strong>Order ID:</strong> {order.orderId}</Typography>
                                <Typography><strong>Date:</strong> {dayjs(order.timestamp).format('YYYY-MM-DD HH:mm')}</Typography>
                                <Typography><strong>Total:</strong> Rs {parseFloat(order.total || 0).toFixed(2)}</Typography>
                                <Divider sx={{ my: 1 }} />
                                {(order.items || []).map((item, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {item.name} x {item.quantity} = Rs {(parseFloat(item.price) * item.quantity).toFixed(2)}
                                    </Typography>
                                ))}
                            </Card>
                        ))
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ReportDashboard;
